import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  increment,
  writeBatch,
  runTransaction,
  Transaction,
} from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  signOut,
  getAuth,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import { db, auth, firebaseConfig } from '../lib/firebase';
import {
  IBackend,
  AdminProduct,
  AdminCategory,
  Coupon,
  Affiliate,
  Customer,
  Quote,
  Sale,
  QuoteWithPayment,
  CustomerDetails,
  AnalyticsData,
  StoreConfig,
  MenuItem,
  Page,
  Permission,
  BlogPost,
  Review,
  Question,
} from './backend';
import { User } from '../types/auth';
import { logger } from '../utils/logger';

export class FirebaseBackend implements IBackend {
  private cleanObject(obj: any): any {
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map((v) => this.cleanObject(v));

    const newObj: any = {};
    Object.keys(obj).forEach((key) => {
      if (obj[key] !== undefined) {
        newObj[key] = this.cleanObject(obj[key]);
      }
    });
    return newObj;
  }

  // --- Products ---
  async getProducts(): Promise<AdminProduct[]> {
    const snapshot = await getDocs(collection(db, 'products'));
    return snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }) as AdminProduct);
  }

  async getProduct(id: string): Promise<AdminProduct | undefined> {
    const docRef = doc(db, 'products', id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? ({ ...docSnap.data(), id: docSnap.id } as AdminProduct) : undefined;
  }

  async createProduct(
    product: Omit<AdminProduct, 'id' | 'createdAt' | 'updatedAt' | 'clicks'>
  ): Promise<AdminProduct> {
    const id = crypto.randomUUID();
    const newProduct: AdminProduct = this.cleanObject({
      ...product,
      id,
      clicks: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    await setDoc(doc(db, 'products', id), newProduct);
    return newProduct;
  }

  async updateProduct(id: string, updates: Partial<AdminProduct>): Promise<AdminProduct | null> {
    const docRef = doc(db, 'products', id);
    const cleanedUpdates = this.cleanObject(updates);
    await updateDoc(docRef, { ...cleanedUpdates, updatedAt: new Date().toISOString() });
    const updated = await this.getProduct(id);
    return updated || null;
  }

  async deleteProduct(id: string): Promise<void> {
    await deleteDoc(doc(db, 'products', id));
  }

  async incrementProductClicks(id: string): Promise<void> {
    const docRef = doc(db, 'products', id);
    await updateDoc(docRef, { clicks: increment(1) });
    this.trackAnalytics('clicks');
  }

  async resetAllProductClicks(): Promise<void> {
    const snapshot = await getDocs(collection(db, 'products'));
    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { clicks: 0 });
    });
    await batch.commit();
  }

  // --- Categories ---
  async getCategories(): Promise<AdminCategory[]> {
    const snapshot = await getDocs(collection(db, 'categories'));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as AdminCategory);
  }

  async getCategory(id: string): Promise<AdminCategory | undefined> {
    const docRef = doc(db, 'categories', id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? ({ id: docSnap.id, ...docSnap.data() } as AdminCategory) : undefined;
  }

  async createCategory(
    category: Omit<AdminCategory, 'id' | 'createdAt' | 'updatedAt' | 'productCount'>
  ): Promise<AdminCategory> {
    // Validate unique slug
    const existingQuery = query(collection(db, 'categories'), where('slug', '==', category.slug));
    const existingSnap = await getDocs(existingQuery);
    if (!existingSnap.empty) {
      throw new Error('Uma categoria com este slug já existe');
    }

    const id = crypto.randomUUID();
    const newCategory: AdminCategory = this.cleanObject({
      ...category,
      id,
      productCount: 0,
      order: category.order ?? 0,
      isActive: category.isActive ?? true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    await setDoc(doc(db, 'categories', id), newCategory);
    return newCategory;
  }

  async updateCategory(id: string, updates: Partial<AdminCategory>): Promise<AdminCategory | null> {
    // If updating slug, validate uniqueness
    if (updates.slug) {
      const existingQuery = query(collection(db, 'categories'), where('slug', '==', updates.slug));
      const existingSnap = await getDocs(existingQuery);
      const existingDocs = existingSnap.docs.filter((d) => d.id !== id);
      if (existingDocs.length > 0) {
        throw new Error('Uma categoria com este slug já existe');
      }
    }

    const docRef = doc(db, 'categories', id);
    const cleanedUpdates = this.cleanObject(updates);
    await updateDoc(docRef, { ...cleanedUpdates, updatedAt: new Date().toISOString() });
    const updated = await this.getCategory(id);
    return updated || null;
  }

  async deleteCategory(id: string): Promise<void> {
    // Check if category has products
    const productsQuery = query(collection(db, 'products'), where('categoryId', '==', id));
    const productsSnap = await getDocs(productsQuery);
    if (!productsSnap.empty) {
      throw new Error(
        'Não é possível excluir uma categoria que possui produtos. Mova ou exclua os produtos primeiro.'
      );
    }

    // Check if category has children
    const childrenQuery = query(collection(db, 'categories'), where('parentId', '==', id));
    const childrenSnap = await getDocs(childrenQuery);
    if (!childrenSnap.empty) {
      throw new Error(
        'Não é possível excluir uma categoria que possui subcategorias. Exclua as subcategorias primeiro.'
      );
    }

    await deleteDoc(doc(db, 'categories', id));
  }

  // --- Coupons ---
  async getCoupons(): Promise<Coupon[]> {
    const snapshot = await getDocs(collection(db, 'coupons'));
    return snapshot.docs.map((doc) => doc.data() as Coupon);
  }

  async createCoupon(coupon: Omit<Coupon, 'id' | 'usedCount'>): Promise<Coupon> {
    const id = crypto.randomUUID();
    const newCoupon: Coupon = this.cleanObject({ ...coupon, id, usedCount: 0 });
    await setDoc(doc(db, 'coupons', id), newCoupon);
    return newCoupon;
  }

  async updateCoupon(id: string, updates: Partial<Coupon>): Promise<Coupon | null> {
    const docRef = doc(db, 'coupons', id);
    await updateDoc(docRef, this.cleanObject(updates));
    const snap = await getDoc(docRef);
    return snap.exists() ? (snap.data() as Coupon) : null;
  }

  async deleteCoupon(id: string): Promise<void> {
    await deleteDoc(doc(db, 'coupons', id));
  }

  // --- Affiliates ---
  async getAffiliates(): Promise<Affiliate[]> {
    const snapshot = await getDocs(collection(db, 'affiliates'));
    return snapshot.docs.map((doc) => doc.data() as Affiliate);
  }

  async createAffiliate(
    affiliate: Omit<Affiliate, 'id' | 'createdAt' | 'clicks' | 'conversions' | 'totalSales'>
  ): Promise<Affiliate> {
    const id = crypto.randomUUID();
    const newAffiliate: Affiliate = {
      ...affiliate,
      id,
      clicks: 0,
      conversions: 0,
      totalSales: 0,
      createdAt: new Date().toISOString(),
    };
    await setDoc(doc(db, 'affiliates', id), newAffiliate);
    return newAffiliate;
  }

  async updateAffiliate(id: string, updates: Partial<Affiliate>): Promise<Affiliate | null> {
    const docRef = doc(db, 'affiliates', id);
    await updateDoc(docRef, this.cleanObject(updates));
    const snap = await getDoc(docRef);
    return snap.exists() ? (snap.data() as Affiliate) : null;
  }

  async deleteAffiliate(id: string): Promise<void> {
    await deleteDoc(doc(db, 'affiliates', id));
  }

  async getAffiliateByCode(code: string): Promise<Affiliate | undefined> {
    const q = query(collection(db, 'affiliates'), where('code', '==', code));
    const snapshot = await getDocs(q);
    return !snapshot.empty ? (snapshot.docs[0].data() as Affiliate) : undefined;
  }

  async incrementAffiliateClicks(id: string): Promise<void> {
    const docRef = doc(db, 'affiliates', id);
    await updateDoc(docRef, { clicks: increment(1) });
  }

  // --- Customers ---
  async getCustomers(): Promise<Customer[]> {
    const snapshot = await getDocs(collection(db, 'customers'));
    return snapshot.docs.map((doc) => doc.data() as Customer);
  }

  // --- Quotes ---
  async getQuotes(): Promise<Quote[]> {
    const snapshot = await getDocs(collection(db, 'quotes'));
    return snapshot.docs.map((doc) => doc.data() as Quote);
  }

  async createQuote(quote: Omit<Quote, 'id' | 'createdAt' | 'status'>): Promise<Quote> {
    const ref = doc(collection(db, 'quotes'));
    const id = ref.id;
    const newQuote: Quote = this.cleanObject({
      ...quote,
      id,
      createdAt: new Date().toISOString(),
      status: 'pending',
    });
    await setDoc(doc(db, 'quotes', id), newQuote);

    // Update/Create Customer
    // Update/Create Customer
    let customerId = '';
    try {
      const q = query(collection(db, 'customers'), where('whatsapp', '==', quote.customerWhatsapp));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const customerDoc = querySnapshot.docs[0];
        customerId = customerDoc.id;
        const customerData = customerDoc.data() as Customer;
        const newClicked = [...customerData.clickedProducts];
        quote.items.forEach((item) => {
          if (!newClicked.includes(item.productId)) newClicked.push(item.productId);
        });

        // Update customer info if provided in quote
        const customerUpdates: Partial<Customer> = { clickedProducts: newClicked };
        if (quote.customerEmail) customerUpdates.email = quote.customerEmail;
        if (quote.customerAddress) customerUpdates.address = quote.customerAddress;
        if (quote.customerCity) customerUpdates.city = quote.customerCity;
        if (quote.customerState) customerUpdates.state = quote.customerState;
        if (quote.customerCpfCnpj) customerUpdates.cpfCnpj = quote.customerCpfCnpj;

        await updateDoc(customerDoc.ref, customerUpdates);
      } else {
        customerId = crypto.randomUUID();
        const newCustomer: Customer = {
          id: customerId,
          name: quote.customerName,
          whatsapp: quote.customerWhatsapp,
          email: quote.customerEmail,
          address: quote.customerAddress,
          city: quote.customerCity,
          state: quote.customerState,
          cpfCnpj: quote.customerCpfCnpj,
          clickedProducts: quote.items.map((i) => i.productId),
          createdAt: new Date().toISOString(),
        };
        await setDoc(doc(db, 'customers', customerId), newCustomer);
      }
    } catch (error) {
      console.error('Error linking/creating customer in createQuote:', error);
      // Don't throw, allow quote to succeed even if customer creation fails (e.g. permissions)
    }

    // Update quote with correct customerId if found/created
    if (customerId && newQuote.customerId !== customerId) {
      await updateDoc(doc(db, 'quotes', id), { customerId });
      newQuote.customerId = customerId;
    }

    this.trackAnalytics('quotes');
    return newQuote;
  }

  async updateQuote(id: string, updates: Partial<Quote>): Promise<Quote | null> {
    const docRef = doc(db, 'quotes', id);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;

    const currentData = snap.data() as Quote;
    if (updates.status === 'completed' && currentData.status !== 'completed') {
      this.trackAnalytics('sales');
    }

    const cleanedUpdates = this.cleanObject(updates);
    await updateDoc(docRef, cleanedUpdates);
    return { ...currentData, ...cleanedUpdates };
  }

  // --- Analytics ---
  async getAnalytics(): Promise<AnalyticsData[]> {
    const snapshot = await getDocs(
      query(collection(db, 'analytics'), orderBy('date', 'desc'), limit(30))
    );
    return snapshot.docs.map((doc) => doc.data() as AnalyticsData).reverse();
  }

  async getDashboardMetrics(startDate?: Date, endDate?: Date): Promise<any> {
    // This is heavy for client-side, ideally should be an aggregation query or cloud function
    // For now, fetching all analytics
    const analytics = await this.getAnalytics();
    // Filter in memory
    let filtered = analytics;
    if (startDate && endDate) {
      filtered = analytics.filter((a) => {
        const d = new Date(a.date);
        return d >= startDate && d <= endDate;
      });
    }

    const totalClicks = filtered.reduce((acc, curr) => acc + curr.clicks, 0);
    const totalWhatsapp = filtered.reduce((acc, curr) => acc + curr.whatsappClicks, 0);
    const totalQuotes = filtered.reduce((acc, curr) => acc + (curr.quotes || 0), 0);
    const totalSales = filtered.reduce((acc, curr) => acc + (curr.sales || 0), 0);

    // Low stock - need to fetch products
    // Optimization: maybe keep a counter or separate collection for low stock
    const productsSnap = await getDocs(query(collection(db, 'products'), where('stock', '<', 10)));
    const lowStockProducts = productsSnap.size;

    return { totalClicks, totalWhatsapp, totalQuotes, totalSales, lowStockProducts };
  }

  async incrementWhatsappClicks(id: string): Promise<void> {
    this.trackAnalytics('whatsappClicks');
  }

  async trackVisit(): Promise<void> {
    const today = new Date().toISOString().split('T')[0];
    const docRef = doc(db, 'analytics', today);
    const snap = await getDoc(docRef);

    if (snap.exists()) {
      await updateDoc(docRef, {
        views: increment(1),
      });
    } else {
      await setDoc(docRef, {
        date: today,
        clicks: 0,
        whatsappClicks: 0,
        views: 1,
        quotes: 0,
        sales: 0,
      });
    }
  }

  private async trackAnalytics(metric: 'clicks' | 'whatsappClicks' | 'quotes' | 'sales') {
    const today = new Date().toISOString().split('T')[0];
    const docRef = doc(db, 'analytics', today);
    const snap = await getDoc(docRef);

    if (snap.exists()) {
      await updateDoc(docRef, {
        [metric]: increment(1),
        // views: (metric === 'clicks' || metric === 'whatsappClicks') ? increment(1) : increment(0) // REMOVED: Views tracked separately via trackVisit (Session)
      });
    } else {
      await setDoc(docRef, {
        date: today,
        clicks: metric === 'clicks' ? 1 : 0,
        whatsappClicks: metric === 'whatsappClicks' ? 1 : 0,
        views: 0, // VISITS handles this now
        quotes: metric === 'quotes' ? 1 : 0,
        sales: metric === 'sales' ? 1 : 0,
      });
    }
  }

  // --- Store Config ---
  async getStoreConfig(): Promise<StoreConfig> {
    const docRef = doc(db, 'config', 'store');
    const snap = await getDoc(docRef);
    if (snap.exists()) return snap.data() as StoreConfig;

    // Return default if not exists (or seed it)
    return {
      name: 'Alpha Estética',
      description: 'Loja de produtos estéticos profissionais',
      colors: { primary: '#1a1a1a', secondary: '#C5A059', accent: '#f3f4f6' },
      banners: { hero: [], heroInterval: 5000, mid: [], footer: [] },
      benefitsStrip: { enabled: true, items: [] },
      topbar: {
        enabled: true,
        animated: true,
        text: '',
        backgroundColor: '#000',
        textColor: '#fff',
      },
      buyButton: { text: 'Comprar', icon: 'ShoppingCart', showIcon: true },
      integrations: {
        googleAnalytics: '',
        facebookPixel: '',
        cloudinary: { cloudName: '', apiKey: '', apiSecret: '' },
      },
      contacts: { whatsapp: '', email: '', phone: '', address: '' },
      educationBlock: {
        enabled: true,
        tag: 'MENTORIAS E CURSOS',
        title: 'Eleve sua carreira com a',
        highlightedTitle: 'Mentoria Alpha Estética',
        description:
          'Potencialize seus resultados clínicos com nossos programas exclusivos de educação, liderados pela Dra. Ariana Marinari.',
        backgroundImage:
          'https://res.cloudinary.com/dkml49llq/image/upload/v1764287337/FOTO_BOA_PARA_CAPA_FUNDO_CLARO_PESSOA_POSICIONADA_A_DIREITA_jbuvnv.webp',
        mobileBackgroundImage: '',
        gradientFrom: 'white',
        gradientTo: 'transparent',
        items: [
          {
            icon: 'graduation' as const,
            title: 'Curso IPOG: Harmonização Facial',
            description:
              'Pós-graduação e extensão universitária com certificação reconhecida nacionalmente.',
          },
          {
            icon: 'star' as const,
            title: 'Mentoria VIP Hands-on',
            description:
              'Imersão prática intensiva com pacientes reais e supervisão direta da Dra. Ariana.',
          },
        ],
        primaryButton: {
          text: 'Conheça a Embaixadora',
          link: '/embaixadora',
          backgroundColor: '#C9A961',
          textColor: 'white',
        },
        secondaryButton: {
          text: 'Ver Cursos',
          link: '/cursos-ipog',
          borderColor: '#d1d5db',
          textColor: '#374151',
        },
      },
    };
  }

  async updateStoreConfig(config: Partial<StoreConfig>): Promise<StoreConfig> {
    const docRef = doc(db, 'config', 'store');
    await setDoc(docRef, this.cleanObject(config), { merge: true });
    return this.getStoreConfig();
  }

  // --- Menus ---
  async getMenus(): Promise<MenuItem[]> {
    const snapshot = await getDocs(query(collection(db, 'menus'), orderBy('order')));
    return snapshot.docs.map((doc) => doc.data() as MenuItem);
  }

  async updateMenus(menus: MenuItem[]): Promise<MenuItem[]> {
    const batch = writeBatch(db);
    // Delete all existing (simplistic approach)
    const snapshot = await getDocs(collection(db, 'menus'));
    snapshot.docs.forEach((d) => batch.delete(d.ref));

    menus.forEach((m) => {
      const ref = doc(collection(db, 'menus')); // Auto ID
      batch.set(ref, this.cleanObject(m));
    });
    await batch.commit();
    return menus;
  }

  // --- Pages ---
  async getPages(): Promise<Page[]> {
    const snapshot = await getDocs(collection(db, 'pages'));
    return snapshot.docs.map((doc) => doc.data() as Page);
  }

  async getPage(id: string): Promise<Page | undefined> {
    const docRef = doc(db, 'pages', id);
    const snap = await getDoc(docRef);
    return snap.exists() ? (snap.data() as Page) : undefined;
  }

  async getPageBySlug(slug: string): Promise<Page | undefined> {
    const q = query(collection(db, 'pages'), where('slug', '==', slug));
    const snap = await getDocs(q);
    return !snap.empty ? (snap.docs[0].data() as Page) : undefined;
  }

  async createPage(page: Omit<Page, 'id' | 'createdAt' | 'updatedAt'>): Promise<Page> {
    const id = crypto.randomUUID();
    const newPage: Page = {
      ...page,
      id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await setDoc(doc(db, 'pages', id), newPage);
    return newPage;
  }

  async updatePage(id: string, updates: Partial<Page>): Promise<Page | null> {
    const docRef = doc(db, 'pages', id);
    const cleanedUpdates = this.cleanObject(updates);
    await updateDoc(docRef, { ...cleanedUpdates, updatedAt: new Date().toISOString() });
    const updated = await this.getPage(id);
    return updated || null;
  }

  async deletePage(id: string): Promise<void> {
    await deleteDoc(doc(db, 'pages', id));
  }

  // --- Users ---
  async login(email: string, password: string): Promise<{ user: any; token: string } | null> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Fetch additional user data from Firestore if needed (e.g. role)
      // For now assuming role is stored in a 'users' collection
      let userData: any = {};
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        userData = userDoc.exists() ? userDoc.data() : {};
      } catch (e) {
        console.warn(
          'Could not fetch user profile from Firestore, proceeding with auth defaults.',
          e
        );
      }

      return {
        user: {
          id: user.uid,
          email: user.email,
          name: userData.name || user.displayName || 'Admin',
          role: userData.role || 'admin',
          avatar: userData.avatar || user.photoURL,
        },
        token: await user.getIdToken(),
      };
    } catch (error) {
      console.error('Login error', error);
      throw error;
    }
  }

  async getUsers(): Promise<User[]> {
    const snapshot = await getDocs(collection(db, 'users'));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as User);
  }

  async createUser(user: Omit<User, 'id'>, password?: string): Promise<User> {
    let id: string = crypto.randomUUID();

    if (password) {
      // Create user in Firebase Auth using a secondary app instance
      // to avoid signing out the current admin user.
      const secondaryApp = initializeApp(firebaseConfig, 'Secondary');
      const secondaryAuth = getAuth(secondaryApp);

      try {
        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          user.email,
          password
        );
        id = userCredential.user.uid;
        await signOut(secondaryAuth);
      } catch (error) {
        console.error('Error creating auth user:', error);
        throw error;
      } finally {
        await deleteApp(secondaryApp);
      }
    }

    const newUser = this.cleanObject({ ...user, id });
    await setDoc(doc(db, 'users', id), newUser);
    return newUser as User;
  }

  async updateUser(id: string, updates: Partial<User>): Promise<User | null> {
    const docRef = doc(db, 'users', id);
    await updateDoc(docRef, this.cleanObject(updates));
    const snap = await getDoc(docRef);
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as User) : null;
  }

  async deleteUser(id: string): Promise<void> {
    await deleteDoc(doc(db, 'users', id));
  }

  // --- Permissions ---
  async getPermissions(): Promise<Permission[]> {
    const snapshot = await getDocs(collection(db, 'permissions'));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Permission);
  }

  async createPermission(permission: Omit<Permission, 'id'>): Promise<Permission> {
    const id = crypto.randomUUID();
    const newPermission = { ...permission, id };
    await setDoc(doc(db, 'permissions', id), newPermission);
    return newPermission;
  }

  async updatePermission(id: string, updates: Partial<Permission>): Promise<Permission | null> {
    const docRef = doc(db, 'permissions', id);
    await updateDoc(docRef, updates);
    const snap = await getDoc(docRef);
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as Permission) : null;
  }

  async deletePermission(id: string): Promise<void> {
    await deleteDoc(doc(db, 'permissions', id));
  }

  // --- Sales ---
  async getSales(): Promise<Sale[]> {
    const snapshot = await getDocs(collection(db, 'sales'));
    return snapshot.docs.map((doc) => doc.data() as Sale);
  }

  // --- Counters ---
  async getNextSequence(counterName: string): Promise<number> {
    const counterRef = doc(db, 'counters', counterName);

    try {
      return await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);

        let nextId = 1000; // Start at 1000

        if (counterDoc.exists()) {
          const currentId = counterDoc.data().currentId;
          nextId = currentId + 1;
        }

        transaction.set(counterRef, { currentId: nextId }, { merge: true });
        return nextId;
      });
    } catch (error) {
      console.error('Error generating sequence ID:', error);
      // Fallback to random ID if transaction fails (should be rare)
      return Math.floor(Math.random() * 90000) + 10000;
    }
  }

  async createSale(sale: Omit<Sale, 'id' | 'createdAt'>): Promise<Sale> {
    // Use numeric ID
    const numericId = await this.getNextSequence('sales');
    const id = numericId.toString(); // Keep as string to match interface but store number content

    const ref = doc(collection(db, 'sales'));
    // We use the firestore generated ID for the document key, but 'id' field is our custom numeric ID
    // WAIT: The app likely relies on sale.id being the document ID for updates.
    // Let's use the numeric ID as the document ID too, to make it easier to find.

    const newSale: Sale = this.cleanObject({
      ...sale,
      id: id,
      createdAt: new Date().toISOString(),
    });

    // Use setDoc with specific ID instead of addDoc (which makes random ID)
    await setDoc(doc(db, 'sales', id), newSale);
    this.trackAnalytics('sales');
    return newSale;
  }

  async updateSale(id: string, updates: Partial<Sale>): Promise<Sale | null> {
    const docRef = doc(db, 'sales', id);
    await updateDoc(docRef, this.cleanObject(updates));
    const snap = await getDoc(docRef);
    return snap.exists() ? (snap.data() as Sale) : null;
  }

  async syncMercadoPagoPayment(paymentId: string): Promise<Sale | null> {
    try {
      // Get Mercado Pago credentials from store config
      const config = await this.getStoreConfig();
      if (!config.integrations.mercadopago?.accessToken) {
        throw new Error('Mercado Pago not configured');
      }

      // Fetch payment status from Mercado Pago API
      const response = await fetch(`https://api.mercadopago.com/v1/payments/${paymentId}`, {
        headers: {
          Authorization: `Bearer ${config.integrations.mercadopago.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment status from Mercado Pago');
      }

      const paymentData = await response.json();

      // Find sale by payment ID
      const sales = await this.getSales();
      const sale = sales.find((s) => s.paymentId === paymentId);

      if (!sale) {
        throw new Error('Sale not found');
      }

      // Update sale with Mercado Pago data
      const updates: Partial<Sale> = {
        paymentStatus: paymentData.status,
        mercadoPagoData: {
          id: paymentData.id,
          status: paymentData.status,
          status_detail: paymentData.status_detail,
          payment_type_id: paymentData.payment_type_id,
          date_approved: paymentData.date_approved,
          date_created: paymentData.date_created,
          transaction_amount: paymentData.transaction_amount,
        },
      };

      const updatedSale = await this.updateSale(sale.id, updates);

      // Deduct stock if approved
      if (paymentData.status === 'approved' && sale.paymentStatus !== 'approved') {
        await this.deductStock(sale.id);
      }

      return updatedSale;
    } catch (error) {
      console.error('Error syncing Mercado Pago payment:', error);
      throw error;
    }
  }

  async deductStock(saleId: string): Promise<void> {
    try {
      const saleRef = doc(db, 'sales', saleId);
      const saleSnap = await getDoc(saleRef);

      if (!saleSnap.exists()) {
        console.error('Sale not found for stock deduction');
        return;
      }

      const sale = saleSnap.data() as Sale;

      // Check if stock already deducted (optional flag, or just rely on 'approved' transition)
      // For now, assume this is called only once upon approval.

      const batch = writeBatch(db);
      const productsCache: Record<string, AdminProduct> = {};

      for (const item of sale.items) {
        // Fetch product if not cached
        if (!productsCache[item.productId]) {
          const pDoc = await getDoc(doc(db, 'products', item.productId));
          if (pDoc.exists()) {
            productsCache[item.productId] = { id: pDoc.id, ...pDoc.data() } as AdminProduct;
          }
        }

        const product = productsCache[item.productId];
        if (!product) continue;

        if (product.isKit && product.kitItems) {
          // Deduct from components
          for (const kitItem of product.kitItems) {
            const componentRef = doc(db, 'products', kitItem.productId);
            // Optimize: verify if validation is needed or just atomic decrement
            // We use decrement but ensure it doesn't go below 0?
            // Firestore increment(-N) works fine.
            batch.update(componentRef, { stock: increment(-(kitItem.quantity * item.quantity)) });
          }
        } else {
          // Deduct simple product
          // Only if it HAS stock management (some might be infinite?)
          // Assuming all have 'stock' field. Use increment(-qty)
          const productRef = doc(db, 'products', item.productId);
          batch.update(productRef, { stock: increment(-item.quantity) });
        }
      }

      await batch.commit();
      logger.info(`Stock deducted for sale ${saleId}`);
    } catch (error) {
      console.error('Error deducting stock:', error);
      // Don't throw, just log. Or consider how to handle partial failure?
      // Firestore batch is atomic, so partial failure logic is fine.
    }
  }

  // --- Customer Details ---
  async getCustomerDetails(id: string): Promise<CustomerDetails | undefined> {
    const docRef = doc(db, 'customers', id);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return undefined;

    const customer = snap.data() as Customer;
    const quotes = await this.getCustomerQuotes(id);
    const sales = await this.getCustomerSales(id);
    const totalSpent = sales.reduce((sum, sale) => sum + sale.totalValue, 0);
    const lastPurchaseDate =
      sales.length > 0
        ? sales.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
            .createdAt
        : undefined;

    return {
      ...customer,
      quotes,
      sales,
      totalSpent,
      lastPurchaseDate,
    };
  }

  async updateCustomer(id: string, updates: Partial<Customer>): Promise<Customer | null> {
    const docRef = doc(db, 'customers', id);
    await updateDoc(docRef, updates);
    const snap = await getDoc(docRef);
    return snap.exists() ? (snap.data() as Customer) : null;
  }

  async getCustomerQuotes(customerId: string): Promise<Quote[]> {
    const q = query(collection(db, 'quotes'), where('customerId', '==', customerId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as Quote);
  }

  async getCustomerSales(customerId: string): Promise<Sale[]> {
    const q = query(collection(db, 'sales'), where('customerId', '==', customerId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as Sale);
  }

  async deleteCustomer(id: string): Promise<void> {
    await deleteDoc(doc(db, 'customers', id));
  }

  // --- Reviews ---
  async createReview(review: Omit<Review, 'id' | 'createdAt' | 'likes'>): Promise<Review> {
    const id = crypto.randomUUID();
    const newReview: Review = {
      ...review,
      id,
      createdAt: new Date().toISOString(),
      likes: 0,
      status: review.status || 'pending',
    };
    await setDoc(doc(db, 'reviews', id), newReview);
    return newReview;
  }

  async getProductReviews(productId: string): Promise<Review[]> {
    const q = query(
      collection(db, 'reviews'),
      where('productId', '==', productId),
      where('status', '==', 'approved')
    );
    const snapshot = await getDocs(q);
    const reviews = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Review);
    // Sort in memory to avoid requiring a composite index
    return reviews.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  async getAllReviews(): Promise<Review[]> {
    const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Review);
  }

  async updateReviewStatus(
    id: string,
    status: 'pending' | 'approved' | 'rejected',
    reply?: string
  ): Promise<Review | null> {
    const docRef = doc(db, 'reviews', id);
    const updates: Partial<Review> = { status };
    if (reply) {
      updates.reply = reply;
      updates.repliedAt = new Date().toISOString();
    }
    await updateDoc(docRef, updates);
    const updatedSnap = await getDoc(docRef);

    if (updatedSnap.exists()) {
      const review = updatedSnap.data() as Review;
      // Update product rating if status changed to/from approved
      await this.updateProductRating(review.productId);

      // Generate Coupon if approved
      if (status === 'approved') {
        await this.generateReviewCoupon(review.customerName);
      }

      return review;
    }
    return null;
  }

  async approveAllReviews(productId: string): Promise<void> {
    const q = query(
      collection(db, 'reviews'),
      where('productId', '==', productId),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) return;

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      const docRef = docSnap.ref;
      batch.update(docRef, { status: 'approved' });
    });

    await batch.commit();

    // Update product rating after bulk approval
    await this.updateProductRating(productId);
  }

  private async generateReviewCoupon(customerName: string) {
    // Simple logic: create a unique coupon for 5% off
    const code = `AVAL${Math.floor(Math.random() * 10000)}`;
    const coupon = {
      code,
      type: 'percent' as const,
      value: 5,
      status: 'active' as const,
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
      maxUses: 1,
      usedCount: 0,
    };
    await this.createCoupon(coupon);
  }

  async deleteReview(id: string): Promise<void> {
    const docRef = doc(db, 'reviews', id);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const review = snap.data() as Review;
      await deleteDoc(docRef);
      await this.updateProductRating(review.productId);
    } else {
      await deleteDoc(docRef);
    }
  }

  async likeReview(id: string): Promise<void> {
    const docRef = doc(db, 'reviews', id);
    await updateDoc(docRef, { likes: increment(1) });
  }

  private async updateProductRating(productId: string) {
    try {
      const reviews = await this.getProductReviews(productId);
      const count = reviews.length;
      const total = reviews.reduce((acc, r) => acc + r.rating, 0);
      const average = count > 0 ? parseFloat((total / count).toFixed(1)) : 0;

      await this.updateProduct(productId, {
        rating: average,
        reviewsCount: count,
      });
    } catch (error) {
      console.error('Error updating product rating:', error);
    }
  }

  // --- Questions ---
  async createQuestion(question: Omit<Question, 'id' | 'createdAt' | 'status'>): Promise<Question> {
    const id = crypto.randomUUID();
    const newQuestion: Question = {
      ...question,
      id,
      createdAt: new Date().toISOString(),
      status: 'pending',
    };
    await setDoc(doc(db, 'questions', id), newQuestion);
    return newQuestion;
  }

  async getProductQuestions(productId: string): Promise<Question[]> {
    const q = query(
      collection(db, 'questions'),
      where('productId', '==', productId),
      where('status', '==', 'approved')
    );
    const snapshot = await getDocs(q);
    const questions = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Question);
    // Sort in memory to avoid requiring a composite index
    return questions.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  async getAllQuestions(): Promise<Question[]> {
    const q = query(collection(db, 'questions'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Question);
  }

  async answerQuestion(id: string, answer: string): Promise<Question | null> {
    const docRef = doc(db, 'questions', id);
    await updateDoc(docRef, {
      answer,
      answeredAt: new Date().toISOString(),
      status: 'approved', // Auto approve when answered
    });
    const updated = await getDoc(docRef);
    return updated.exists() ? (updated.data() as Question) : null;
  }

  async deleteQuestion(id: string): Promise<void> {
    await deleteDoc(doc(db, 'questions', id));
  }

  async approveQuestion(id: string): Promise<Question | null> {
    const docRef = doc(db, 'questions', id);
    await updateDoc(docRef, { status: 'approved' });
    const updated = await getDoc(docRef);
    return updated.exists() ? (updated.data() as Question) : null;
  }
  async getQuoteMetrics(startDate?: Date, endDate?: Date): Promise<any> {
    const quotes = await this.getQuotes();
    let filtered = quotes;

    if (startDate && endDate) {
      filtered = quotes.filter((q) => {
        const d = new Date(q.createdAt);
        return d >= startDate && d <= endDate;
      });
    }

    return {
      total: filtered.length,
      pending: filtered.filter((q) => q.status === 'pending').length,
      responded: filtered.filter((q) => q.status === 'responded').length,
      completed: filtered.filter((q) => q.status === 'completed').length,
      cancelled: filtered.filter((q) => q.status === 'cancelled').length,
      totalValue: filtered.reduce((sum, q) => sum + q.totalValue, 0),
    };
  }

  async getSaleMetrics(startDate?: Date, endDate?: Date): Promise<any> {
    const sales = await this.getSales();
    let filtered = sales;

    if (startDate && endDate) {
      filtered = sales.filter((s) => {
        const d = new Date(s.createdAt);
        return d >= startDate && d <= endDate;
      });
    }

    return {
      total: filtered.length,
      pending: filtered.filter((s) => s.paymentStatus === 'pending').length,
      approved: filtered.filter((s) => s.paymentStatus === 'approved').length,
      rejected: filtered.filter((s) => s.paymentStatus === 'rejected').length,
      totalValue: filtered.reduce((sum, s) => sum + s.totalValue, 0),
    };
  }

  // --- Migration Helper ---

  async clearDatabase() {
    const collections = [
      'products',
      'categories',
      'customers',
      'quotes',
      'coupons',
      'analytics',
      'menus',
      'pages',
      'users',
    ];
    for (const colName of collections) {
      const snapshot = await getDocs(collection(db, colName));
      const batch = writeBatch(db);
      let count = 0;
      for (const doc of snapshot.docs) {
        batch.delete(doc.ref);
        count++;
        if (count >= 400) {
          await batch.commit();
          count = 0;
        }
      }
      if (count > 0) await batch.commit();
    }
    logger.info('Database cleared.');
  }

  async migrateFromLocalStorage(): Promise<string> {
    const stored = localStorage.getItem('alphamed_db_v3');
    if (!stored) {
      console.warn('No local data found to migrate.');
      return 'no_data';
    }

    const data = JSON.parse(stored);
    logger.debug('Found local data:', data);

    // --- PATCHING LOGIC START ---

    // Fix stale benefits strip
    if (
      data.storeConfig?.benefitsStrip?.items?.length === 3 &&
      data.storeConfig.benefitsStrip.items[0].icon === 'Truck'
    ) {
      data.storeConfig.benefitsStrip.items = [
        {
          icon: 'Award',
          title: 'Qualidade Premium',
          subtitle: 'Produtos certificados e aprovados pela ANVISA',
        },
        { icon: 'Truck', title: 'Envio Ágil', subtitle: 'Logística eficiente para todo o Brasil' },
        {
          icon: 'ShieldCheck',
          title: 'Compra Segura',
          subtitle: 'Ambiente protegido e confiável para sua clínica',
        },
        {
          icon: 'Headphones',
          title: 'Suporte Técnico',
          subtitle: 'Consultoria especializada para profissionais',
        },
      ];
    }

    // Fix stale Hero title and add tag
    if (data.storeConfig?.banners?.hero?.[0]) {
      const hero = data.storeConfig.banners.hero[0];
      if (hero.title === 'Excelência em Estética') {
        hero.title = 'Ciência e Luxo em | Harmonia Perfeita';
        hero.tag = 'Excelência em Estética';
      }
      if (!hero.tag) hero.tag = 'Excelência em Estética';
      if (!hero.align) hero.align = 'center';
    }

    // Add Ambassador Banner
    if (data.storeConfig?.banners?.hero) {
      const hasAmbassador = data.storeConfig.banners.hero.some(
        (b: any) => b.link === '/embaixadora'
      );
      if (!hasAmbassador) {
        data.storeConfig.banners.hero.push({
          image:
            'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=2070&auto=format&fit=crop',
          title: 'Nossa Embaixadora: | Dra. Ariana Marinari',
          subtitle:
            'Biomédica Esteta, fundadora da Harmô Conceito — referência em harmonização e educação clínica.',
          link: '/embaixadora',
          tag: 'Parceria Exclusiva',
          align: 'left',
          buttonText: 'Conheça a Mentoria',
        });
      }
    }

    // --- PATCHING LOGIC END ---

    // Ensure storeConfig is migrated correctly
    if (data.storeConfig) {
      logger.info('Migrating Store Config:', data.storeConfig);
    } else {
      console.warn('No storeConfig found in local data.');
    }

    // Clear existing data to prevent duplicates
    await this.clearDatabase();

    await this.performMigration(data);
    return 'success';
  }

  async seedDatabase() {
    // Default Seed Data (Same as LocalBackend)
    const PRODUCTS_SEED = [
      {
        id: '1',
        name: 'Ácido Hialurônico Reticulado 1ml',
        description: 'Preenchedor dérmico de alta viscosidade para contorno facial e volumização.',
        price: 450.0,
        salePrice: 450.0,
        image:
          'https://images.unsplash.com/photo-1612817288484-96916a0816a9?auto=format&fit=crop&q=80&w=800',
        category: 'Preenchedores',
        categoryId: '1',
        stock: 100,
        clicks: 0,
        isProfessionalLine: true,
        status: 'active',
        images: [
          'https://images.unsplash.com/photo-1612817288484-96916a0816a9?auto=format&fit=crop&q=80&w=800',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: '2',
        name: 'Toxina Botulínica Tipo A 100U',
        description: 'Toxina botulínica liofilizada para uso estético e terapêutico.',
        price: 890.0,
        salePrice: 890.0,
        image:
          'https://images.unsplash.com/photo-1579165466741-7f35a4755657?auto=format&fit=crop&q=80&w=800',
        category: 'Toxinas',
        categoryId: '2',
        stock: 50,
        clicks: 0,
        isProfessionalLine: true,
        status: 'active',
        images: [
          'https://images.unsplash.com/photo-1579165466741-7f35a4755657?auto=format&fit=crop&q=80&w=800',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: '3',
        name: 'Bioestimulador de Colágeno',
        description: 'Estimulador de colágeno injetável para flacidez corporal e facial.',
        price: 1200.0,
        salePrice: 1200.0,
        image:
          'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&q=80&w=800',
        category: 'Bioestimuladores',
        categoryId: '3',
        stock: 30,
        clicks: 0,
        isProfessionalLine: true,
        status: 'active',
        images: [
          'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&q=80&w=800',
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];

    const CATEGORIES_SEED = [
      {
        id: '1',
        name: 'Preenchedores',
        description: 'Ácidos hialurônicos',
        slug: 'preenchedores',
        productCount: 1,
      },
      {
        id: '2',
        name: 'Toxinas',
        description: 'Toxinas botulínicas',
        slug: 'toxinas',
        productCount: 1,
      },
      {
        id: '3',
        name: 'Bioestimuladores',
        description: 'Estimuladores de colágeno',
        slug: 'bioestimuladores',
        productCount: 1,
      },
      { id: '4', name: 'Fios', description: 'Fios de sustentação', slug: 'fios', productCount: 0 },
    ];

    const STORE_CONFIG_SEED = {
      colors: { primary: '#1a1a1a', secondary: '#C5A059', accent: '#f3f4f6' },
      banners: {
        hero: [
          {
            image:
              'https://res.cloudinary.com/dwkqdnp4h/image/upload/v1764596677/dannjgocwzk6bdgimjli.webp',
            title: 'Ciência e Luxo em | Harmonia Perfeita',
            subtitle: 'Alpha Estética apresenta sua linha estética profissional.',
            link: '/produtos',
            tag: 'Excelência em Estética',
            align: 'center',
            buttonText: 'Conhecer Catálogo',
          },
          {
            image:
              'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=2070&auto=format&fit=crop',
            title: 'Nossa Embaixadora: | Dra. Ariana Marinari',
            subtitle:
              'Biomédica Esteta, fundadora da Harmô Conceito — referência em harmonização e educação clínica.',
            link: '/embaixadora',
            tag: 'Parceria Exclusiva',
            align: 'left',
            buttonText: 'Conheça a Mentoria',
          },
          {
            image:
              'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070&auto=format&fit=crop',
            title: 'Tecnologia Avançada | Para Sua Clínica',
            subtitle: 'Equipamentos e insumos de alta performance para os melhores resultados.',
            link: '/produtos/canetas-dispositivos',
            tag: 'Inovação',
            align: 'right',
            buttonText: 'Ver Equipamentos',
          },
        ],
        heroInterval: 5000,
        mid: [],
        footer: [],
      },
      benefitsStrip: {
        enabled: true,
        items: [
          { icon: 'Award', title: 'Qualidade Premium', subtitle: 'Produtos certificados' },
          { icon: 'Truck', title: 'Envio Ágil', subtitle: 'Logística eficiente' },
          { icon: 'ShieldCheck', title: 'Compra Segura', subtitle: 'Ambiente protegido' },
          { icon: 'Headphones', title: 'Suporte Técnico', subtitle: 'Consultoria especializada' },
        ],
      },
      topbar: {
        enabled: true,
        animated: true,
        text: 'Frete Grátis acima de R$ 500,00',
        backgroundColor: '#1a1a1a',
        textColor: '#ffffff',
      },
      buyButton: { text: 'Comprar', icon: 'ShoppingCart', showIcon: true },
      integrations: {
        googleAnalytics: '',
        facebookPixel: '',
        cloudinary: { cloudName: '', apiKey: '', apiSecret: '' },
      },
      contacts: {
        whatsapp: '5562992213411',
        email: 'alphamedestetica@gmail.com',
        phone: '062 3277-6347',
        address:
          'Alpha Estética Hospitalar\nRua J 46 SN, Quadra 046, Lote 20, Galpão 01\nMansões Paraíso, Aparecida de Goiânia - GO\nCEP: 74952-335',
      },
    };

    const data = {
      products: PRODUCTS_SEED,
      categories: CATEGORIES_SEED,
      storeConfig: STORE_CONFIG_SEED,
      coupons: [],
      customers: [],
      quotes: [],
      analytics: [],
      menus: [
        { id: '1', label: 'Início', url: '/', type: 'header', order: 0 },
        { id: '2', label: 'Sobre', url: '/sobre', type: 'header', order: 1 },
        { id: '3', label: 'Catálogo', url: '/produtos', type: 'header', order: 2 },
        { id: '4', label: 'Contato', url: '/contato', type: 'header', order: 3 },
      ],
      pages: [],
      users: [],
    };

    await this.clearDatabase();
    await this.performMigration(data);
    logger.info('Database seeded with default data!');
  }

  private async performMigration(data: any) {
    let batch = writeBatch(db);
    let opCount = 0;

    const checkCommit = async () => {
      if (opCount >= 400) {
        // Firestore batch limit is 500
        await batch.commit();
        batch = writeBatch(db);
        opCount = 0;
      }
    };

    // Products
    if (data.products && Array.isArray(data.products)) {
      for (const p of data.products) {
        const ref = p.id ? doc(db, 'products', p.id) : doc(collection(db, 'products'));
        const productData = this.cleanObject({ ...p, id: ref.id });
        batch.set(ref, productData);
        opCount++;
        await checkCommit();
      }
    }

    // Categories
    if (data.categories && Array.isArray(data.categories)) {
      for (const c of data.categories) {
        const ref = c.id ? doc(db, 'categories', c.id) : doc(collection(db, 'categories'));
        const catData = { ...c, id: ref.id };
        batch.set(ref, catData);
        opCount++;
        await checkCommit();
      }
    }

    // Customers
    if (data.customers && Array.isArray(data.customers)) {
      for (const c of data.customers) {
        const ref = c.id ? doc(db, 'customers', c.id) : doc(collection(db, 'customers'));
        const custData = { ...c, id: ref.id };
        batch.set(ref, custData);
        opCount++;
        await checkCommit();
      }
    }

    // Quotes
    if (data.quotes && Array.isArray(data.quotes)) {
      for (const q of data.quotes) {
        const ref = q.id ? doc(db, 'quotes', q.id) : doc(collection(db, 'quotes'));
        const quoteData = { ...q, id: ref.id };
        batch.set(ref, quoteData);
        opCount++;
        await checkCommit();
      }
    }

    // Store Config
    if (data.storeConfig) {
      const ref = doc(db, 'config', 'store');
      batch.set(ref, data.storeConfig);
      opCount++;
      await checkCommit();
    }

    // Coupons
    if (data.coupons && Array.isArray(data.coupons)) {
      for (const c of data.coupons) {
        const ref = c.id ? doc(db, 'coupons', c.id) : doc(collection(db, 'coupons'));
        const couponData = { ...c, id: ref.id };
        batch.set(ref, couponData);
        opCount++;
        await checkCommit();
      }
    }

    // Analytics
    if (data.analytics && Array.isArray(data.analytics)) {
      for (const a of data.analytics) {
        const ref = doc(db, 'analytics', a.date);
        batch.set(ref, a);
        opCount++;
        await checkCommit();
      }
    }

    // Menus
    if (data.menus && Array.isArray(data.menus)) {
      for (const m of data.menus) {
        const ref = m.id ? doc(db, 'menus', m.id) : doc(collection(db, 'menus'));
        const menuData = { ...m, id: ref.id };
        batch.set(ref, menuData);
        opCount++;
        await checkCommit();
      }
    }

    // Pages
    if (data.pages && Array.isArray(data.pages)) {
      for (const p of data.pages) {
        const ref = p.id ? doc(db, 'pages', p.id) : doc(collection(db, 'pages'));
        const pageData = { ...p, id: ref.id };
        batch.set(ref, pageData);
        opCount++;
        await checkCommit();
      }
    }

    // Users
    if (data.users && Array.isArray(data.users)) {
      for (const u of data.users) {
        const ref = u.id ? doc(db, 'users', u.id) : doc(collection(db, 'users'));
        const userData = { ...u, id: ref.id };
        batch.set(ref, userData);
        opCount++;
        await checkCommit();
      }
    }

    // Commit any remaining operations
    if (opCount > 0) {
      await batch.commit();
    }
  }

  // ===== Blog Posts =====

  async getBlogPosts(): Promise<BlogPost[]> {
    const snapshot = await getDocs(collection(db, 'blogPosts'));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as BlogPost);
  }

  async getBlogPost(id: string): Promise<BlogPost | undefined> {
    const docRef = doc(db, 'blogPosts', id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? ({ id: docSnap.id, ...docSnap.data() } as BlogPost) : undefined;
  }

  async getBlogPostBySlug(slug: string): Promise<BlogPost | undefined> {
    const q = query(collection(db, 'blogPosts'), where('slug', '==', slug));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return undefined;
    const docSnap = snapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() } as BlogPost;
  }

  async createBlogPost(
    post: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt' | 'views'>
  ): Promise<BlogPost> {
    const now = new Date().toISOString();
    const newPost: Omit<BlogPost, 'id'> = {
      ...post,
      views: 0,
      createdAt: now,
      updatedAt: now,
    };
    const cleanPost = this.cleanObject(newPost);
    const docRef = await addDoc(collection(db, 'blogPosts'), cleanPost);
    return { id: docRef.id, ...newPost } as BlogPost;
  }

  async updateBlogPost(id: string, updates: Partial<BlogPost>): Promise<BlogPost | null> {
    const docRef = doc(db, 'blogPosts', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;

    const updatedData = this.cleanObject({
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    await updateDoc(docRef, updatedData);
    const updated = await getDoc(docRef);
    return { id: updated.id, ...updated.data() } as BlogPost;
  }

  async deleteBlogPost(id: string): Promise<void> {
    await deleteDoc(doc(db, 'blogPosts', id));
  }

  async incrementBlogPostViews(id: string): Promise<void> {
    const docRef = doc(db, 'blogPosts', id);
    await updateDoc(docRef, { views: increment(1) });
  }

  async seedProductsFromConstants(): Promise<string> {
    console.warn('Seeding from constants has been deprecated and removed.');
    return 'Função depreciada e removida.';
  }
}
